import { CARD_ICONS } from "../utils/leaveConstants";

interface LeaveStatsCardsProps {
    leaveBalances: any[];
}

const LeaveStatsCards: React.FC<LeaveStatsCardsProps> = ({ leaveBalances }) => {
    const renderUnlimitedCard = (leave: any, index: number) => {
        const icon = CARD_ICONS[index % 4];
        return (
            <div className="lt-card card" key={leave.leaveTypeId || index}>
                <div className="card-body pb-3 bg-white">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                        <div className="text-start">
                            <p className="mb-1 text-muted text-uppercase lt-name">
                                {leave.leaveTypeName}
                                <span className="lt-unlimited-badge">
                                    <i className="ti ti-infinity" style={{ fontSize: "16px" }} />
                                </span>
                            </p>
                            <div className="text-muted fs-14">
                                <i className="ti ti-circle-check" /> Used: {leave.usedLeaves || 0}{" "}
                                {leave.usedLeaves === 1 ? "day" : "days"}
                            </div>
                        </div>
                        <div className="flex-shrink-0">
                            <span
                                className="lt-icon shadow-sm"
                                style={{
                                    background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                                    color: "#059669",
                                }}
                            >
                                <i className={`ti ${icon} fs-24`} />
                            </span>
                        </div>
                    </div>
                    <div
                        style={{
                            fontSize: "12px",
                            color: "#64748b",
                            marginTop: "6px",
                            padding: "8px",
                            background: "rgba(16, 185, 129, 0.05)",
                            borderRadius: "8px",
                            border: "1px dashed rgba(16, 185, 129, 0.2)",
                        }}
                    >
                        <div style={{ marginTop: "6px", color: "#f59e0b" }}>
                            These leaves are not paid. Use when paid leaves are exhausted or
                            additional leave is needed.
                        </div>
                    </div>
                    <div className="mt-2">
                        <span className="badge badge-md d-flex badge-info-transparent align-items-center justify-content-center">
                            <i className="ti ti-clock me-1" />
                            Pending Approvals: {leave.pendingLeaves}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderRegularCard = (leave: any, index: number) => {
        const icon = CARD_ICONS[index % 4];
        const isMonthlyAccrual = leave.isMonthlyAccrual;
        const totalAccumulated = Number(leave.totalAccumulated) || 0;
        const remaining = Math.max(0, Number(leave.balance) || 0);
        const currentMonthTotal = Number(leave.currentMonthTotal) || 0;
        const currentMonthAllocated = Number(leave.currentMonthAllocated) || 0;
        const carryForwardFromPrevMonth = Number(leave.carryForwardFromPrevMonth) || 0;
        const currentMonthUsed = Number(leave.currentMonthUsed) || 0;
        const monthlyAllocationRate = Number(leave.monthlyAllocation) || 0;

        const baseForPercentage = currentMonthTotal > 0
            ? currentMonthTotal
            : (isMonthlyAccrual ? currentMonthAllocated : Number(leave.yearlyTotal) || 0);

        const pct =
            baseForPercentage > 0
                ? Math.round((currentMonthUsed / baseForPercentage) * 100)
                : 0;

        const usageClass =
            pct < 50 ? "pg-success" : pct < 80 ? "pg-warning" : "pg-danger";
        const isPending = currentMonthAllocated === 0 && isMonthlyAccrual;

        return (
            <div className="lt-card card" key={leave.leaveTypeId || index}>
                <div className="card-body bg-white">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                        <div className="text-start">
                            <p className="mb-1 text-muted text-uppercase lt-name">
                                {leave.leaveTypeName}
                                {isMonthlyAccrual && (
                                    <span className="lt-monthly-badge">MONTHLY</span>
                                )}
                                {carryForwardFromPrevMonth > 0 && (
                                    <span className="lt-cf-badge">CF: {carryForwardFromPrevMonth}</span>
                                )}
                            </p>
                            <h4 className="mb-0">
                                {totalAccumulated}
                                <small className="text-muted fs-12 ms-1">available</small>
                            </h4>
                            {!isPending && (isMonthlyAccrual || carryForwardFromPrevMonth > 0) && (
                                <div className="lt-info-text">
                                    <i className="ti ti-info-circle" style={{ fontSize: "12px" }} />
                                    {isMonthlyAccrual ? `Allocated: ${monthlyAllocationRate}/month | ` : ""} Carry Forward:{" "}
                                    {carryForwardFromPrevMonth}
                                </div>
                            )}
                        </div>
                        <div className="flex-shrink-0">
                            <span className="lt-icon shadow-sm">
                                <i className={`ti ${icon} fs-24`} />
                            </span>
                        </div>
                    </div>

                    {!isPending && (
                        <>
                            <div
                                className="lt-progress mb-2"
                                title={`Used ${currentMonthUsed}/${currentMonthTotal}`}
                            >
                                <div
                                    className={`lt-progress-bar ${usageClass}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted fs-14">
                                    Used: {currentMonthUsed}/{currentMonthTotal}
                                </span>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <span
                                    className={`badge badge-md ${pct >= 100 ? "bg-danger" : pct >= 80 ? "bg-warning" : "bg-success"
                                        }`}
                                >
                                    {pct}% used
                                </span>
                                <span className="badge badge-md d-flex badge-info-transparent align-items-center">
                                    <i className="ti ti-alert-circle me-1" />
                                    Remaining: {remaining}
                                </span>
                            </div>
                        </>
                    )}

                    {isPending && (
                        <div
                            style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginTop: "8px",
                                padding: "8px",
                                background: "rgba(245, 158, 11, 0.05)",
                                borderRadius: "8px",
                                border: "1px dashed rgba(245, 158, 11, 0.2)",
                            }}
                        >
                            <div style={{ marginBottom: "4px" }}>
                                <strong>Monthly Allocation:</strong> {monthlyAllocationRate}/month
                            </div>
                            <div>Leaves will be allocated automatically</div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderLeaveCard = (leave: any, index: number) => {
        const isUnlimited = leave.unlimited === true || leave.balance === -1;
        return isUnlimited
            ? renderUnlimitedCard(leave, index)
            : renderRegularCard(leave, index);
    };

    return (
        <>
            <style>
                {`
          .lt-card { border: 1px solid #eef2f7; border-radius: 14px; overflow: hidden; transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
          .lt-card:hover { transform: translateY(-2px); border-color: #dbe6ff; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08); }
          .lt-name { letter-spacing: .04em; font-weight: 600; display: flex; align-items: center; gap: 6px; }
          .lt-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, #eef4ff, #f0fff7); color: #334155; }
          .lt-progress { width: 70%; height: 8px; background: rgb(147, 158, 170); border-radius: 999px; position: relative; overflow: hidden; }
          .lt-progress-bar { height: 100%; border-radius: 999px; transition: width .3s ease; background: linear-gradient(90deg, #60a5fa, #34d399); }
          .lt-progress-bar.pg-success { background: linear-gradient(90deg, #34d399, #10b981); }
          .lt-progress-bar.pg-warning { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
          .lt-progress-bar.pg-danger { background: linear-gradient(90deg, #f87171, #ef4444); }
          .lt-monthly-badge { font-size: 9px; padding: 2px 6px; border-radius: 4px; font-weight: 500; background: rgba(59, 130, 246, 0.1); color: #2563eb; border: 1px solid rgba(59, 130, 246, 0.2); }
          .lt-cf-badge { font-size: 9px; padding: 2px 6px; border-radius: 4px; font-weight: 500; background: rgba(139, 92, 246, 0.1); color: #7c3aed; border: 1px solid rgba(139, 92, 246, 0.2); }
          .lt-unlimited-badge { font-size: 9px; padding: 2px 6px; border-radius: 4px; font-weight: 500; background: rgba(16, 185, 129, 0.15); color: #059669; border: 1px solid rgba(16, 185, 129, 0.3); }
          .lt-info-text { font-size: 11px; color: #64748b; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
          .fs-12 { font-size: 12px; }
          .fs-14 { font-size: 14px; }
          .fs-24 { font-size: 24px; }
        `}
            </style>

            <div className="row">
                {leaveBalances.map((leave: any, index: number) => (
                    <div className="col-xl-3 col-md-6" key={leave.leaveTypeId || index}>
                        {renderLeaveCard(leave, index)}
                    </div>
                ))}
            </div>
        </>
    );
};

export default LeaveStatsCards;
